import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import MainLayout from '@/components/MainLayout'
import { useAuth } from '@/contexts/AuthContext'
import { ApiService } from '@/lib/api'
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea'
import { storageService } from '@/services/storageService'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'

const Home: React.FC = () => {
  const { t } = useTranslation(['steps', 'errors'])
  const [prompt, setPrompt] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useLocalizedNavigate()
  const { user, isGuest } = useAuth()
  const placeholder = t('steps:home.placeholder')
  const textareaRef = useAutoResizeTextarea(prompt, placeholder)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!prompt.trim()) return
    // Allow submission in guest mode or with authenticated user
    if (!isGuest && !user) return

    setIsSubmitting(true)
    setError(null)

    try {
      // Create a new prompt record with the original_prompt
      const data = await storageService.createPrompt({
          original_prompt: prompt.trim(),
          context: { questions: [], user_input: '', context_output: '' }, // Will be filled immediately
          role: { generated_roles: [], selected_role: '' }, // Will be filled in background
          action: '', // Will be filled in background
          format: {}, // Will be filled during onboarding
          tone_scores: {}, // Will be filled during onboarding
          additional_tones: [], // Will be filled during onboarding
          example: '', // Will be filled during onboarding
          final_prompt: '' // Will be filled during onboarding
      }, user)

      console.log('Prompt created successfully:', data)

      // Generate context questions immediately
      try {
        const contextResponse = await ApiService.generateContextQuestions(prompt.trim())
        if (contextResponse.success && contextResponse.questions.length > 0) {
          // Update the prompt with context questions
          await storageService.updatePrompt(data.id, {
              context: { 
                questions: contextResponse.questions, 
                user_input: '', 
                context_output: '' 
              }
          }, user)

          // Generate role and action in background (non-blocking)
          Promise.all([
            ApiService.generateRoleSuggestions(prompt.trim())
              .then(async (roleResponse) => {
                if (roleResponse.success && roleResponse.roles.length > 0) {
                  await storageService.updatePrompt(data.id, {
                      role: { 
                        generated_roles: roleResponse.roles, 
                        selected_role: '' 
                      }
                  }, user)
                }
              })
              .catch((roleError) => {
                console.error('Failed to generate role suggestions:', roleError)
              }),
            
            ApiService.generateActionInstruction(prompt.trim())
              .then(async (actionResponse) => {
                if (actionResponse.success && actionResponse.action) {
                  await storageService.updatePrompt(data.id, {
                    action: actionResponse.action
                  }, user)
                }
              })
              .catch((actionError) => {
                console.error('Failed to generate action instruction:', actionError)
              })
          ]).catch((error) => {
            console.error('Error in background generation:', error)
          })
        }
      } catch (contextError) {
        console.error('Failed to generate context questions:', contextError)
        // Continue to onboarding even if context generation fails
      }

      // Navigate to onboarding with the prompt ID
      navigate('/onboarding', { state: { promptId: data.id } })
    } catch (err) {
      console.error('Error creating prompt:', err)
      setError(t('errors:createPrompt'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <MainLayout>
      <div className="flex flex-col items-center justify-center px-4 flex-1 h-full">
        <div className="max-w-2xl w-full text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 sm:mb-8">
            {t('steps:home.title')}
          </h1>

          <form onSubmit={handleSubmit} className="relative">
            <div className="relative">
              <textarea
                ref={textareaRef}
                placeholder={t('steps:home.placeholder')}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isSubmitting}
                className="w-full min-h-12 pl-4 pr-16 pt-3 pb-3 text-sm sm:text-lg border border-gray-300 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows={1}
              />
              <Button
                type="submit"
                disabled={!prompt.trim() || isSubmitting}
                className="absolute right-2 top-2 h-8 w-8 p-0 rounded-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300"
              >
                {isSubmitting ? (
                  <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg 
                    className="w-4 h-4 text-white" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5 10l7-7m0 0l7 7m-7-7v18" 
                    />
                  </svg>
                )}
              </Button>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-xs sm:text-sm">{error}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </MainLayout>
  )
}

export default Home
